/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Immutable value object describing a server-driven logging window in
 * epoch seconds. Range semantics are start-inclusive, end-exclusive
 * (`start <= now < end`). A window with `end < start` is invalid and
 * cannot be constructed.
 */
export class LoggingWindow {
  private static readonly WINDOW_PATTERN: RegExp = /^(\d+)-(\d+)$/;

  private readonly mStartEpochSec: number;
  private readonly mEndEpochSec: number;

  private constructor(startEpochSec: number, endEpochSec: number) {
    this.mStartEpochSec = startEpochSec;
    this.mEndEpochSec = endEpochSec;
  }

  /**
   * Parses a raw `"<startEpochSec>-<endEpochSec>"` value into a window.
   * Returns null when the value is missing, malformed, or `end < start`.
   *
   * @param raw - The raw window string (or null when absent)
   * @returns A valid window, or null
   */
  public static parse(raw: string | null): LoggingWindow | null {
    if (raw === null) {
      return null;
    }

    const match: RegExpMatchArray | null = raw
      .trim()
      .match(LoggingWindow.WINDOW_PATTERN);
    if (match === null) {
      return null;
    }

    return LoggingWindow.fromEpochSeconds(
      Number.parseInt(match[1], 10),
      Number.parseInt(match[2], 10)
    );
  }

  /**
   * Builds a window from numeric epoch-second bounds.
   * Returns null when either bound is not a finite integer or `end < start`.
   *
   * @param startEpochSec - Inclusive start bound
   * @param endEpochSec - Exclusive end bound
   * @returns A valid window, or null
   */
  public static fromEpochSeconds(
    startEpochSec: number,
    endEpochSec: number
  ): LoggingWindow | null {
    if (
      !Number.isInteger(startEpochSec) ||
      !Number.isInteger(endEpochSec) ||
      endEpochSec < startEpochSec
    ) {
      return null;
    }

    return new LoggingWindow(startEpochSec, endEpochSec);
  }

  public getStartEpochSec(): number {
    return this.mStartEpochSec;
  }

  public getEndEpochSec(): number {
    return this.mEndEpochSec;
  }

  /**
   * Evaluates whether the supplied wall-clock time falls inside the
   * window (start-inclusive, end-exclusive).
   *
   * @param nowEpochSec - Current wall-clock time in epoch seconds
   * @returns true when `start <= now < end`
   */
  public isInWindow(nowEpochSec: number): boolean {
    return (
      nowEpochSec >= this.mStartEpochSec && nowEpochSec < this.mEndEpochSec
    );
  }
}
